/* assets/cart-frame-link.js
 * Cart hygiene for the poster/frame bundle (WP-4). A frame add-on creates two cart lines
 * linked by a shared hidden `_bundle_id` property (see assets/frame-addon.js): the poster
 * carries `_bundle_role=poster`, the frame carries `_bundle_role=frame` + `_frame_size`. An
 * orphaned frame line is a direct refund/support cost, so this file:
 *
 *   1. Removes a frame automatically when its linked poster is removed.
 *   2. Mirrors a poster's new quantity onto its linked frame.
 *   3. Renders an "add a frame" row under any frame-eligible poster line that has none yet
 *      (the row markup itself is emitted by sections/main-cart-items.liquid and
 *      snippets/cart-drawer.liquid -- this file only wires the click).
 *
 * We do not touch assets/cart.js or assets/cart-drawer.js (Dawn's own cart-items /
 * cart-drawer-items classes). Instead we hook the same PUB_SUB_EVENTS.cartUpdate event Dawn
 * already publishes after every quantity change or removal (see cart.js's updateQuantity),
 * diff the bundle pairing against what it was before that update, and issue a follow-up
 * /cart/update.js call -- keyed by line item `key`, never by index -- to fix any pairing the
 * user's action just broke. That keeps every remove/quantity control exactly as Dawn built
 * it; we only ever react after the fact.
 *
 * Every entry point below is wrapped so a failure degrades to "do nothing more" rather than
 * ever throwing into Dawn's own cart flow -- this is the most conversion-critical surface in
 * the store.
 */
(function () {
  "use strict";

  if (window.__TBPCartFrameLinkBooted) return;
  window.__TBPCartFrameLinkBooted = true;

  if (!window.routes || !window.routes.cart_update_url || !window.routes.cart_add_url || !window.routes.cart_change_url || !window.routes.cart_url) {
    return; // no cart routes -- nothing safe to do
  }

  // ============================================================
  // Helpers
  // ============================================================
  function safeFetchConfig() {
    try {
      return fetchConfig();
    } catch (e) {
      return { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" } };
    }
  }

  function safePublishCartUpdate(cartData) {
    try {
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-frame-link", cartData: cartData });
      }
    } catch (e) {
      /* not fatal -- the fetch that produced cartData already reflects the real cart */
    }
  }

  // Same composition rule as assets/frame-addon.js's composeForLabel, duplicated here since
  // we don't import across asset files.
  function composeForLabel(title, size) {
    var a = String(title || "").trim();
    var b = String(size || "").trim();
    if (a && b) return a + " " + b;
    return a || b;
  }

  function randomBundleId() {
    return "b_" + Math.random().toString(36).slice(2, 8);
  }

  function fetchJSON(url, opts) {
    return fetch(url, opts).then(function (r) {
      return r.json();
    });
  }

  function isCartError(data) {
    // Shopify's cart endpoints return {status, message, description} on error instead of a
    // cart object with an `items` array.
    return !data || !Array.isArray(data.items);
  }

  // ============================================================
  // Bundle map: bundle_id -> { posterKey, posterQty, frameKey, frameQty }
  // ============================================================
  var bundleMap = {};

  function mapFromCartItems(items) {
    var map = {};
    (items || []).forEach(function (item) {
      var props = item.properties || {};
      var id = props["_bundle_id"];
      if (!id) return;
      var role = props["_bundle_role"];
      if (!map[id]) map[id] = {};
      if (role === "poster") {
        map[id].posterKey = item.key;
        map[id].posterQty = item.quantity;
      } else if (role === "frame") {
        map[id].frameKey = item.key;
        map[id].frameQty = item.quantity;
      }
    });
    return map;
  }

  function scanDomForBundles() {
    var map = {};
    var rows = document.querySelectorAll("[data-bundle-id]");
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var id = row.getAttribute("data-bundle-id");
      var role = row.getAttribute("data-bundle-role");
      var key = row.getAttribute("data-key");
      if (!id || !key) continue;
      var qtyInput = row.querySelector(".quantity__input");
      var qty = qtyInput ? parseInt(qtyInput.value, 10) : NaN;
      if (!map[id]) map[id] = {};
      if (role === "poster") {
        map[id].posterKey = key;
        map[id].posterQty = qty;
      } else if (role === "frame") {
        map[id].frameKey = key;
        map[id].frameQty = qty;
      }
    }
    return map;
  }

  bundleMap = scanDomForBundles();

  function findBundleIdForPosterKey(key) {
    var ids = Object.keys(bundleMap);
    for (var i = 0; i < ids.length; i++) {
      if (bundleMap[ids[i]].posterKey === key) return ids[i];
    }
    return null;
  }

  // ============================================================
  // Reconciliation: keep every poster/frame pair in sync after Dawn's own cart-items /
  // cart-drawer-items classes finish a remove or quantity change. Chained through a single
  // promise so overlapping cartUpdate events are processed one at a time, never racing.
  // ============================================================
  var reconcileChain = Promise.resolve();

  function onCartUpdateEvent(event) {
    reconcileChain = reconcileChain.then(function () {
      return reconcile(event);
    }).catch(function () {
      /* one bad event must never block the next */
    });
  }

  function reconcile(event) {
    if (!event || !event.cartData || !Array.isArray(event.cartData.items)) return;

    var newMap = mapFromCartItems(event.cartData.items);
    var updates = {};
    var needsFix = false;
    var idsBeingFixed = [];

    Object.keys(bundleMap).forEach(function (id) {
      var before = bundleMap[id];
      if (!before || !before.posterKey) return; // nothing to reconcile without a poster side

      var after = newMap[id];
      var posterStillPresent = !!(after && after.posterKey);
      var frameStillPresent = !!(after && after.frameKey);

      if (!posterStillPresent && frameStillPresent) {
        // The poster half of this bundle just disappeared -- its frame is now orphaned.
        updates[after.frameKey] = 0;
        needsFix = true;
        idsBeingFixed.push(id);
      } else if (posterStillPresent && frameStillPresent && after.posterQty !== after.frameQty) {
        // The poster's quantity moved; mirror it onto the frame.
        updates[after.frameKey] = after.posterQty;
        needsFix = true;
        idsBeingFixed.push(id);
      }
    });

    if (!needsFix) {
      bundleMap = newMap;
      return;
    }

    // Keep the PRE-fix entries for the bundles we're about to correct (so a failed correction
    // is still recognisable as broken and gets retried on the next cartUpdate event, rather
    // than silently going stale forever), while adopting the fresh state for everything else
    // right away.
    var mergedMap = Object.assign({}, newMap);
    idsBeingFixed.forEach(function (id) {
      mergedMap[id] = bundleMap[id];
    });
    bundleMap = mergedMap;

    return fetchJSON(window.routes.cart_update_url, Object.assign({}, safeFetchConfig(), {
      body: JSON.stringify({ updates: updates }),
    }))
      .then(function (cartData) {
        if (isCartError(cartData)) {
          console.error("cart-frame-link: /cart/update.js returned an error while fixing a poster/frame pairing; will retry on the next cart change.", cartData);
          return;
        }
        bundleMap = mapFromCartItems(cartData.items); // fix succeeded -- fully authoritative now
        return refreshCartIconBubble().then(function () {
          safePublishCartUpdate(cartData);
        });
      })
      .catch(function (e) {
        // Fail safe: the cart itself is untouched (Shopify never received/applied our
        // request), and bundleMap above still remembers this bundle as broken, so the next
        // cartUpdate event (any later remove/quantity change) re-detects the same mismatch
        // and tries again. Until then the orphaned/mismatched line stays in the cart exactly
        // as-is -- never thrown into Dawn's own flow.
        console.error("cart-frame-link: network error while fixing a poster/frame pairing; will retry on the next cart change.", e);
      });
  }

  function refreshCartIconBubble() {
    var bubble = document.getElementById("cart-icon-bubble");
    if (!bubble) return Promise.resolve();
    return fetch(window.routes.cart_url + "?section_id=cart-icon-bubble")
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var source = doc.querySelector(".shopify-section");
        if (source) bubble.innerHTML = source.innerHTML;
      })
      .catch(function () {
        /* the item count badge staying stale for a moment is not worth surfacing an error over */
      });
  }

  try {
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdateEvent);
    }
  } catch (e) {
    /* pub/sub unavailable -- the add-a-frame upsell below still works standalone */
  }

  // ============================================================
  // "Add a frame" upsell row -- catalogue
  // ============================================================
  // sections/main-cart-items.liquid and snippets/cart-drawer.liquid emit one shared
  // #cart-frame-catalog-json blob (not gated to any single product, since the cart holds
  // many different posters) with the RAW catalogue: one entry per product in
  // settings.frames_collection, each carrying its variants' raw option1/option2/option3
  // values. The catalogue comes in two shapes -- one product per colour (a single size
  // option), or one product with BOTH a colour option and a size option (so its variants are
  // the colour x size cross product, e.g. "Svart / 50x70") -- and telling which of a
  // product's options is "size" requires pattern-matching its VALUES (Liquid has no regex),
  // not its name (the store runs sv/en/de). So, same as assets/frame-addon.js does for
  // snippets/frame-picker.liquid, that inspection -- and the one-tile-per-colour grouping it
  // drives -- happens here in JS.
  //
  // NOTE -- deliberately duplicated: assets/frame-addon.js has its own copy of this same
  // size-vs-colour detection (findSizeOptionIndex/looksLikeSize) AND its own normSize, used
  // by the product-page picker. frame-addon.js only loads on product pages; pulling it into
  // the cart too, just for this one helper, isn't worth a second script tag on the store's
  // most conversion-critical page (see the 2026-08-05 conversion audit's first-paint
  // finding). A fix to the size-detection regex or the colour/size grouping logic in ONE of
  // these two files must be applied to the other -- normSize (below) especially: it already
  // drifted once (a unit suffix like " cm" on only one side survived a plain-strip normSize
  // and silently broke every size match against the client's real catalogue).
  var SIZE_VALUE_PATTERN = /\d+\s*[x×]\s*\d+/i;

  function readJSON(id) {
    try {
      var el = document.getElementById(id);
      return JSON.parse((el && el.textContent) || "{}") || {};
    } catch (e) {
      return {};
    }
  }

  // Same normalisation rule as assets/frame-addon.js's normSize, duplicated here since we
  // don't import across asset files: canonicalises "30x40", "30×40", "30 × 40 cm" and
  // "30X40cm" all to "30x40". Extracting the two dimensions and rejoining them (rather than
  // just stripping x/×/whitespace) is deliberate -- a unit suffix like " cm" on one side only
  // (the client's real frame catalogue has it on the frame's size option, but the poster's
  // own _size property doesn't) used to survive a plain strip and leave "3040cm" vs "3040",
  // which never matched. Falls back to the old strip-everything behaviour when the value
  // isn't dimensional at all (e.g. blank), so a genuinely unmatchable size still normalises
  // to a stable (if useless) token rather than throwing.
  function normSize(s) {
    var str = String(s || "").toLowerCase();
    var m = str.match(/(\d+)\s*[x×]\s*(\d+)/);
    if (m) return m[1] + "x" + m[2];
    return str.replace(/[×x\s]/g, "");
  }

  // Builds [{ label, image, sizes: { normSize: {variantId, price, priceFormatted, available, image} } }],
  // one entry per distinct colour across every product in the catalogue.
  function buildCatalogTiles() {
    var raw = readJSON("cart-frame-catalog-json");
    var products = Array.isArray(raw.products) ? raw.products : [];
    var tiles = [];

    products.forEach(function (product) {
      var variants = product.variants || [];
      if (!variants.length) return;

      if ((product.optionsCount || 1) <= 1) {
        // One product per colour: its single option IS the size dimension.
        var sizes = {};
        variants.forEach(function (v) {
          var key = normSize(v.option1);
          if (!key) return;
          sizes[key] = { variantId: v.id, price: v.price, priceFormatted: v.priceFormatted, available: v.available, image: v.image || product.image };
        });
        if (Object.keys(sizes).length) {
          tiles.push({ label: product.labelOverride || product.title || "", image: product.image, sizes: sizes });
        }
        return;
      }

      // Multiple options: find which option index looks like a size by testing its VALUES
      // against the same pattern frame-picker.liquid's own comment documents.
      var sizeIdx = null;
      [1, 2, 3].forEach(function (idx) {
        if (sizeIdx) return;
        var key = "option" + idx;
        var hasSizeValue = variants.some(function (v) {
          return v[key] && SIZE_VALUE_PATTERN.test(v[key]);
        });
        if (hasSizeValue) sizeIdx = idx;
      });
      if (!sizeIdx) return; // can't safely tell size from colour -- skip rather than risk a wrong bind

      var colorIdx = null;
      [1, 2, 3].forEach(function (idx) {
        if (colorIdx || idx === sizeIdx) return;
        if (variants.some(function (v) { return v["option" + idx]; })) colorIdx = idx;
      });
      if (!colorIdx) return;

      var groups = {}; // colour label -> tile
      variants.forEach(function (v) {
        var colorLabel = v["option" + colorIdx];
        var sizeValue = v["option" + sizeIdx];
        if (!colorLabel || !sizeValue) return;
        if (!groups[colorLabel]) groups[colorLabel] = { label: colorLabel, image: v.image || product.image, sizes: {} };
        groups[colorLabel].sizes[normSize(sizeValue)] = { variantId: v.id, price: v.price, priceFormatted: v.priceFormatted, available: v.available, image: v.image || product.image };
        if (!groups[colorLabel].image) groups[colorLabel].image = v.image || product.image;
      });
      Object.keys(groups).forEach(function (label) {
        tiles.push(groups[label]);
      });
    });

    return tiles;
  }

  var CATALOG_TILES = buildCatalogTiles();

  // ============================================================
  // "Add a frame" upsell row -- rendering + interaction
  // ============================================================
  function setTileBusy(tile, busy) {
    tile.disabled = busy;
    tile.classList.toggle("is-loading", !!busy);
  }

  function renderUpsellTiles(container) {
    var slot = container.querySelector("[data-frame-upsell-tiles]");
    if (!slot || slot.children.length || !CATALOG_TILES.length) return;

    var sizeKey = normSize(container.getAttribute("data-size"));
    var soldOutLabel = container.getAttribute("data-sold-out-label") || "";

    CATALOG_TILES.forEach(function (tile) {
      var variant = sizeKey ? tile.sizes[sizeKey] : null;
      var available = !!variant && variant.available !== false;

      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cart-frame-upsell__tile";
      btn.setAttribute("data-frame-upsell-tile", "");
      if (variant) btn.setAttribute("data-variant-id", String(variant.variantId));
      if (!available) btn.disabled = true;

      var thumb = document.createElement("span");
      thumb.className = "cart-frame-upsell__thumb";
      var imgUrl = (variant && variant.image) || tile.image;
      if (imgUrl) {
        var img = document.createElement("img");
        img.src = imgUrl;
        img.width = 52;
        img.height = 52;
        img.loading = "lazy";
        img.alt = tile.label;
        thumb.appendChild(img);
      }
      btn.appendChild(thumb);

      var label = document.createElement("span");
      label.className = "cart-frame-upsell__label";
      label.textContent = tile.label;
      btn.appendChild(label);

      var price = document.createElement("span");
      price.className = "cart-frame-upsell__price";
      price.textContent = available ? "+" + (variant.priceFormatted || "") : soldOutLabel;
      btn.appendChild(price);

      slot.appendChild(btn);
    });
  }

  function renderAllUpsellTiles() {
    var containers = document.querySelectorAll("[data-frame-upsell]");
    for (var i = 0; i < containers.length; i++) renderUpsellTiles(containers[i]);
  }

  renderAllUpsellTiles();

  // Dawn's own cart-items/cart-drawer-items classes wholesale-replace the cart markup after
  // ANY change (not only ones the reconciliation above reacts to), which wipes out any tiles
  // just appended above -- watch for it instead of guessing when that swap lands.
  try {
    if (typeof MutationObserver !== "undefined") {
      ["main-cart-items", "CartDrawer-CartItems"].forEach(function (id) {
        var root = document.getElementById(id);
        if (!root) return;
        new MutationObserver(function () {
          renderAllUpsellTiles();
        }).observe(root, { childList: true, subtree: true });
      });
    }
  } catch (e) {
    /* tiles just won't reappear after a DOM swap -- not fatal */
  }

  document.addEventListener("click", function (evt) {
    var tile = evt.target && evt.target.closest && evt.target.closest("[data-frame-upsell-tile]");
    if (!tile || tile.disabled) return;
    var container = tile.closest("[data-frame-upsell]");
    if (!container) return;
    evt.preventDefault();
    handleAddFrame(container, tile);
  });

  function buildFrameProperties(bundleId, size, forLabel, posterTitle) {
    var props = {
      _bundle_id: bundleId,
      _bundle_role: "frame",
      _frame_size: size,
    };
    if (forLabel) props[forLabel] = composeForLabel(posterTitle, size);
    return props;
  }

  // Gives the new frame line the same `_bundle_id` as its poster. If the poster already has
  // one (it lost a frame earlier via criterion 3 and kept its now-inert bundle id), we just
  // reuse it and touch nothing else. Otherwise we have to write `_bundle_id`/`_bundle_role`
  // onto the poster's *existing* line -- and /cart/change.js's `properties` param replaces
  // the whole properties bag, not merges it. The poster line carries hidden properties (e.g.
  // _preview_url, _design_id, _size, _headline) that never reach the DOM by design, so we
  // cannot round-trip them from anything already on the page. We fetch the authoritative
  // /cart.js first, copy that poster's full properties object, and only then add our two
  // keys -- so nothing on the poster line is ever clobbered.
  function relinkPoster(posterKey, bundleId) {
    return fetchJSON(window.routes.cart_url + ".js")
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var item = null;
        for (var i = 0; i < items.length; i++) {
          if (items[i].key === posterKey) {
            item = items[i];
            break;
          }
        }
        if (!item) return false;

        var props = {};
        var existing = item.properties || {};
        for (var key in existing) {
          if (Object.prototype.hasOwnProperty.call(existing, key)) props[key] = existing[key];
        }
        props["_bundle_id"] = bundleId;
        props["_bundle_role"] = "poster";

        return fetchJSON(window.routes.cart_change_url, Object.assign({}, safeFetchConfig(), {
          body: JSON.stringify({ id: posterKey, quantity: item.quantity, properties: props }),
        })).then(function (data) {
          return !isCartError(data);
        });
      })
      .catch(function () {
        return false;
      });
  }

  function handleAddFrame(container, tile) {
    var variantId = tile.getAttribute("data-variant-id");
    var posterKey = container.getAttribute("data-poster-key");
    if (!variantId || !posterKey) return;

    var posterQty = parseInt(container.getAttribute("data-poster-quantity"), 10) || 1;
    var posterTitle = container.getAttribute("data-poster-title") || "";
    var size = container.getAttribute("data-size") || "";
    var forLabel = container.getAttribute("data-for-property-label") || "";

    setTileBusy(tile, true);

    var existingBundleId = findBundleIdForPosterKey(posterKey);
    var bundleId = existingBundleId || randomBundleId();
    var relink = existingBundleId ? Promise.resolve(true) : relinkPoster(posterKey, bundleId);

    relink
      .then(function (ok) {
        if (!ok) throw new Error("cart-frame-link: poster relink failed");
        return fetchJSON(window.routes.cart_add_url, Object.assign({}, safeFetchConfig(), {
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: posterQty,
                properties: buildFrameProperties(bundleId, size, forLabel, posterTitle),
              },
            ],
          }),
        }));
      })
      .then(function (data) {
        if (data && data.status) throw new Error("cart-frame-link: frame add failed");
        return fetchJSON(window.routes.cart_url + ".js");
      })
      .then(function (cart) {
        if (isCartError(cart)) throw new Error("cart-frame-link: cart refetch failed");
        bundleMap = mapFromCartItems(cart.items);
        return refreshCartIconBubble().then(function () {
          safePublishCartUpdate(cart);
        });
      })
      .catch(function (e) {
        // Fail safe: re-enable the tile so the shopper can just try again. Nothing has been
        // silently left half-linked -- the relink-poster-before-add-frame ordering above
        // means a failure here leaves either the plain, unlinked poster (relink itself
        // failed) or a poster relinked to a bundle id with no frame yet (add failed after a
        // successful relink) -- the latter is the exact harmless, self-healing state
        // criterion 3 already allows for a frame removed on its own.
        console.error("cart-frame-link: add-a-frame failed; cart left as Shopify already has it.", e);
        setTileBusy(tile, false);
      });
  }
})();
